import React, { useRef, useState } from 'react';
import { graphql } from 'gatsby';
import WithSection from '../../components/WithSection/WithSection';
import {
    Head2,
    Body1,
    Head3,
    Headline,
} from '../../components/Typography/Typography';
import Box from '../../components/Box/Box';
import Image from '../../components/Image/Image';
import CtaSlots from '../../slots/CtaSlots/CtaSlots';
import { rem } from 'polished';
import RichText from '../../components/RichText/RichText';
import { css } from 'styled-components';
import { VideoThumbnail } from '../../components/VideoThumbnail/VideoThumbnail';
import Button from '../../components/Button/Button';
import ChevronRight from '../../images/icons/chevron-right.svg';
import BookingFormModal from '../../components/BookingFormModal/BookingFormModal';
import { track } from '../../utils/analytics';
import { log } from 'node:util';
import { getYoutubeIframeTag } from '../../utils/getYoutubeIframeTag';
import { SECTION_VERTICAL_PADDING } from '../../components/SectionWrapper/SectionWrapper';
import useViewport from '../../hooks/useViewport';

const InfoLightSection = ({ data: { primary }, styling: { isDark } }) => {
    const {
        heading,
        subheading,
        heading_tagline,
        cta,
        image,
        top_image,
        video_embed_link,
        watch_a_video_button,
        watch_a_video_button_tracking_event_name,
        inline_video_mp4,
        inline_video_webm,
        split_background,
    } = primary;
    const imageWrapperRef = useRef<HTMLDivElement>();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { isMobile } = useViewport();

    const imgElemHeight =
        imageWrapperRef.current
            ?.querySelector?.('img')
            ?.getBoundingClientRect?.()?.height || 500;

    return (
        <Box
            gridColumn={{ _: '1/-1', lg: '2/-2' }}
            display="flex"
            flexDirection="column"
        >
            <Box
                display="flex"
                flexDirection="column"
                justifyItems="center"
                textAlign="center"
                maxWidth={rem(942)}
                mx="auto"
            >
                {top_image.gatsbyImageData && (
                    <Box mb={rem(40)}>
                        <Image img={top_image} />
                    </Box>
                )}

                <Head3 bold color={isDark ? 'neutral.white' : 'primary.1800'}>
                    {heading.text}
                </Head3>
                {heading_tagline && (
                    <Box my={rem(16)}>
                        <Headline
                            color={isDark ? 'primary.200' : 'neutral.1200'}
                            fontWeight="500"
                        >
                            {heading_tagline}
                        </Headline>
                    </Box>
                )}
                {subheading && (
                    <Body1
                        mt="20px"
                        color={isDark ? 'neutral.white' : 'primary.1600'}
                    >
                        {subheading}
                    </Body1>
                )}
                {cta?.document && (
                    <Box width="100%">
                        <Box
                            display="flex"
                            justifyContent={{ xs: 'center' }}
                            flexDirection={{ _: 'column', xs: 'row' }}
                            mt={rem(32)}
                            gap="16px"
                        >
                            <CtaSlots CTA={cta} />
                            {watch_a_video_button &&
                                watch_a_video_button_tracking_event_name && (
                                    <Button
                                        size="lg"
                                        variant="secondary"
                                        endIcon={<ChevronRight />}
                                        onClick={() => {
                                            setIsModalOpen(true);
                                            track(
                                                watch_a_video_button_tracking_event_name
                                            );
                                        }}
                                    >
                                        {watch_a_video_button}
                                    </Button>
                                )}
                        </Box>
                    </Box>
                )}
            </Box>
            {isModalOpen && video_embed_link.url && (
                <BookingFormModal onClose={() => setIsModalOpen(false)}>
                    <BookingFormModal.FrameContainer
                        onClose={() => setIsModalOpen(false)}
                        dangerouslySetInnerHTML={{
                            __html: getYoutubeIframeTag(video_embed_link.url),
                        }}
                    />
                </BookingFormModal>
            )}

            {split_background && (
                <Box
                    hidden={isMobile}
                    position="absolute"
                    bottom={0}
                    left={0}
                    width="100%"
                    height="35%"
                    bg="neutral.white"
                />
            )}
            {image.gatsbyImageData ? (
                <Box mt="40px" textAlign="center">
                    <VideoThumbnail
                        ref={imageWrapperRef}
                        enableVideo={!watch_a_video_button}
                        video_embed_link={video_embed_link}
                        inlineVideoMp4={inline_video_mp4.url}
                        inlineVideoWebm={inline_video_webm.url}
                        image={image}
                    />
                </Box>
            ) : null}
        </Box>
    );
};

// TODO: convert to fragments
export const _ = graphql`
    fragment INFO_LIGHT_SECTION on PrismicInfoDefault {
        id
        primary {
            heading {
                text
            }
            heading_tagline
            subheading
            cta {
                document {
                    ... on PrismicCta {
                        id
                        data {
                            body {
                                ... on PrismicCtaDataBodyButtons {
                                    id
                                    items {
                                        __typename
                                        text
                                        variant
                                        tracking_event
                                        size
                                        link {
                                            ...PRISMIC_LINK_FRAGMENT
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
            image {
                gatsbyImageData(width: 1100, layout: CONSTRAINED)
            }
            top_image {
                gatsbyImageData(width: 896, layout: CONSTRAINED)
            }
            video_embed_link {
                url
            }
            watch_a_video_button
            watch_a_video_button_tracking_event_name
            split_background
            inline_video_mp4 {
                ...PRISMIC_LINK_FRAGMENT
            }
            inline_video_webm {
                ...PRISMIC_LINK_FRAGMENT
            }
        }
    }
`;

export default WithSection(InfoLightSection);
